import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { radioStations, type RadioStation } from "@/mocks/radios";
import Swal from "sweetalert2";

/* ── Followed Radios ── */
const followedRadioIds = [1, 3, 5, 7, 8, 10, 16, 19];
const followedRadioData = radioStations.filter((s) => followedRadioIds.includes(s.id));

/* ── Followed Programs ── */
const followedPrograms = [
  { id: 1, name: "Carrusel Deportivo", host: "Javier Mendoza", radioName: "Estéreo Capital", frequency: "102.7 FM RFW", listeners: 4230, isLive: true, category: "Deportes", slug: "carrusel-deportivo" },
  { id: 3, name: "Mundo Digital", host: "Isabella Torres", radioName: "Élite Digital", frequency: "96.1 FM RFW", listeners: 2890, isLive: true, category: "Tecnología", slug: "mundo-digital" },
  { id: 5, name: "Economía al Día", host: "Felipe Gutiérrez", radioName: "Peninsular", frequency: "104.9 FM RFW", listeners: 1560, isLive: true, category: "Economía", slug: "economia-al-dia" },
  { id: 6, name: "Éxito Total", host: "Adriana Mejía", radioName: "Éxito FM", frequency: "95.3 FM RFW", listeners: 5120, isLive: true, category: "Entretenimiento", slug: "exito-total" },
  { id: 9, name: "Ruta Rock", host: "Gabriel Paredes", radioName: "Rock Nacional", frequency: "107.1 FM RFW", listeners: 3890, isLive: false, category: "Rock", slug: "ruta-rock" },
  { id: 11, name: "La Hora Joven", host: "Valeria Duque", radioName: "Juvenil FM", frequency: "106.3 FM RFW", listeners: 6340, isLive: true, category: "Pop", slug: "la-hora-joven" },
];

/* ── Followed Hosts ── */
const followedHosts = [
  { id: 1, name: "Javier Mendoza", role: "Locutor Principal", radio: "Deportiva Total", program: "Carrusel Deportivo", followers: 15300, isLive: true, slug: "javier-mendoza" },
  { id: 3, name: "Daniel Ortega", role: "Comentarista", radio: "Deportiva Total", program: "Análisis Táctico", followers: 6400, isLive: true, slug: "daniel-ortega" },
  { id: 7, name: "Isabella Torres", role: "Locutora", radio: "Élite Digital", program: "Mundo Digital", followers: 8200, isLive: true, slug: "isabella-torres" },
  { id: 12, name: "Adriana Mejía", role: "Locutora", radio: "Éxito FM", program: "Éxito Total", followers: 5700, isLive: true, slug: "adriana-mejia" },
  { id: 18, name: "Valeria Duque", role: "DJ", radio: "Juvenil FM", program: "La Hora Joven", followers: 9400, isLive: true, slug: "valeria-duque" },
];

type SectionTab = "radios" | "programs" | "hosts";

export default function AppSiguiendoPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState<SectionTab>("radios");

  const handleUnfollowRadio = useCallback((e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    Swal.fire({
      title: "Dejar de seguir",
      text: `Dejar de seguir a "${name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, dejar de seguir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: { popup: "rounded-2xl", title: "font-bold" },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Dejaste de seguir a "${name}"`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  }, []);

  const handleUnfollowProgram = useCallback((e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    Swal.fire({
      title: "Dejar de seguir",
      text: `Dejar de seguir "${name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, dejar de seguir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: { popup: "rounded-2xl", title: "font-bold" },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Dejaste de seguir "${name}"`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  }, []);

  const handleUnfollowHost = useCallback((e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    Swal.fire({
      title: "Dejar de seguir",
      text: `Dejar de seguir a "${name}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, dejar de seguir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#d1d5db",
      customClass: { popup: "rounded-2xl", title: "font-bold" },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Dejaste de seguir a "${name}"`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  }, []);

  const formatListeners = (n: number): string => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n.toString();

  const tabs = [
    { key: "radios" as SectionTab, label: "Mis Radios", icon: "ri-radio-line", count: followedRadioData.length, activeCount: followedRadioData.filter((r) => r.isLive).length },
    { key: "programs" as SectionTab, label: "Mis Programas", icon: "ri-mic-line", count: followedPrograms.length, activeCount: followedPrograms.filter((p) => p.isLive).length },
    { key: "hosts" as SectionTab, label: "Mis Locutores", icon: "ri-user-voice-line", count: followedHosts.length, activeCount: followedHosts.filter((h) => h.isLive).length },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center">
              <i className="ri-user-follow-fill text-primary-500 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Siguiendo</h1>
              <p className="text-foreground-400 text-[11px]">
                {followedRadioData.length} radios · {followedPrograms.length} programas · {followedHosts.length} locutores
              </p>
            </div>
          </div>

          {/* Quick notification badge */}
          <div className="ml-auto">
            <button
              onClick={() => navigate("/notifications")}
              className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer transition-all relative"
            >
              <i className="ri-notification-3-line"></i>
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-accent-500 rounded-full"></span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* Section Tabs */}
        <div className="sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-4 pb-3">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSection(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  section === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                <i className={`${tab.icon} text-xs`}></i>
                {tab.label}
                <span className="text-[10px] opacity-80">({tab.count})</span>
                {tab.activeCount > 0 && (
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0"></span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Radios Section */}
        {section === "radios" && (
          <div className="mt-1 space-y-2">
            {followedRadioData.length === 0 ? (
              <EmptyState message="No seguís ninguna radio" link="/explorar" linkLabel="Explorar radios" />
            ) : (
              followedRadioData.map((radio) => (
                <div
                  key={radio.id}
                  onClick={() => navigate(`/radio/${radio.frequency.replace(".", "-")}`)}
                  className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
                    <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-foreground-950 truncate">{radio.name}</h3>
                      {radio.isLive && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold shrink-0">
                          <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>
                          LIVE
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{radio.frequency} · {radio.city}, {radio.state}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-foreground-400">
                      <span><i className="ri-headphone-line mr-0.5"></i>{formatListeners(radio.listeners)} oyentes</span>
                      <span>{radio.category}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleUnfollowRadio(e, radio.name)}
                    className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100 shrink-0"
                    title="Dejar de seguir"
                  >
                    <i className="ri-user-unfollow-line text-sm"></i>
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Programs Section */}
        {section === "programs" && (
          <div className="mt-1 space-y-2">
            {followedPrograms.length === 0 ? (
              <EmptyState message="No seguís ningún programa" link="/descubrir" linkLabel="Descubrir programas" />
            ) : (
              followedPrograms.map((prog) => (
                <div
                  key={prog.id}
                  onClick={() => navigate(`/program/${prog.slug}`)}
                  className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                    <i className="ri-mic-fill text-emerald-500 text-lg"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-foreground-950 truncate">{prog.name}</h3>
                      {prog.isLive && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-bold shrink-0">
                          <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                          AL AIRE
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{prog.host} · {prog.radioName} {prog.frequency}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-foreground-400">
                      <span><i className="ri-headphone-line mr-0.5"></i>{formatListeners(prog.listeners)} oyentes</span>
                      <span>{prog.category}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleUnfollowProgram(e, prog.name)}
                    className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100 shrink-0"
                    title="Dejar de seguir"
                  >
                    <i className="ri-user-unfollow-line text-sm"></i>
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Hosts Section */}
        {section === "hosts" && (
          <div className="mt-1 space-y-2">
            {followedHosts.length === 0 ? (
              <EmptyState message="No seguís ningún locutor" link="/descubrir" linkLabel="Descubrir locutores" />
            ) : (
              followedHosts.map((host) => (
                <div
                  key={host.id}
                  onClick={() => navigate(`/host/${host.slug}`)}
                  className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0 text-amber-500 font-bold text-lg">
                    {host.name.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-foreground-950 truncate">{host.name}</h3>
                      {host.isLive && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[9px] font-bold shrink-0">
                          <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse"></span>
                          EN CABINA
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{host.role} · {host.radio}</p>
                    <p className="text-[11px] text-foreground-600">{host.program}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-foreground-400">
                      <span><i className="ri-user-follow-line mr-0.5"></i>{formatListeners(host.followers)} seguidores</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleUnfollowHost(e, host.name)}
                    className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-rose-500 hover:bg-rose-50 transition-all opacity-0 group-hover:opacity-100 shrink-0"
                    title="Dejar de seguir"
                  >
                    <i className="ri-user-unfollow-line text-sm"></i>
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function EmptyState({ message, link, linkLabel }: { message: string; link: string; linkLabel: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
        <i className="ri-user-follow-line text-foreground-300 text-2xl"></i>
      </div>
      <p className="text-foreground-600 text-sm font-medium">{message}</p>
      <button
        onClick={() => navigate(link)}
        className="mt-4 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center gap-2"
      >
        <i className="ri-compass-3-line"></i>
        {linkLabel}
      </button>
    </div>
  );
}